import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Mail, Send, AlertTriangle, Eye, EyeOff, Loader2, Bell, Gift, Share2, Printer,
  RefreshCw, Building, CreditCard, Megaphone, Users, CheckCircle, XCircle, Save,
} from 'lucide-react';

/**
 * Tab "team" – ausgelagerte Tab-Komponente.
 * Erhält alle benötigten State- und Handler-Referenzen vom Parent via Props.
 */
const SettingsTeam = ({ ctx }) => {
  const { t } = useTranslation();
  const {
    settings, setSettings, handleInputChange,
    showSmtpPassword, setShowSmtpPassword,
    testEmailTo, setTestEmailTo, sendingTest, testResult, handleTestEmail,
    triggeringFollowUp, followUpResult, handleTriggerFollowUp,
    triggeringBackup, backupResult, handleTriggerBackup,
  } = ctx;

  return (
    <>

          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-[hsl(var(--primary))]" />
                Social Media & KI-Bilder
              </CardTitle>
              <CardDescription>
                Veröffentlichung, Freigaben, Plattformen und Zugangsdaten zentral im Admin steuern.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'social_posting_enabled', label: 'Social Posting aktivieren' },
                  { key: 'social_admin_approval_required', label: 'Admin-Freigabe erforderlich' },
                  { key: 'social_auto_publish_enabled', label: 'Automatik-Modus aktivieren' },
                  { key: 'social_facebook_enabled', label: 'Facebook aktiv' },
                  { key: 'social_instagram_enabled', label: 'Instagram aktiv' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between py-3 px-4 rounded-lg border border-[hsl(var(--border))] bg-white">
                    <span className="text-sm font-medium">{label}</span>
                    <button
                      type="button"
                      onClick={() => handleInputChange({ target: { name: key, value: !settings[key] } })}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings[key] ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'}`}
                      data-testid={`settings-${key}-toggle`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${settings[key] ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_default_image_source">Standard-Bildquelle</Label>
                <select
                  id="social_default_image_source"
                  name="social_default_image_source"
                  value={settings.social_default_image_source}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-[hsl(var(--border))] bg-white px-3 py-2 text-sm"
                  data-testid="settings-social-default-image-source"
                >
                  <option value="mixed">Eigene Fotos + KI auswählbar</option>
                  <option value="library_only">Nur eigene Fotos / Mediathek</option>
                  <option value="ai_only">Nur KI-Bilder</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="social_openai_api_key">OpenAI API Key für KI-Bilder</Label>
                  <Input id="social_openai_api_key" name="social_openai_api_key" type="password" value={settings.social_openai_api_key} onChange={handleInputChange} placeholder="sk-..." className="font-mono text-sm" data-testid="settings-social-openai-key-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="social_facebook_app_id">Facebook App ID</Label>
                  <Input id="social_facebook_app_id" name="social_facebook_app_id" value={settings.social_facebook_app_id} onChange={handleInputChange} data-testid="settings-social-facebook-app-id" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="social_facebook_app_secret">Facebook App Secret</Label>
                  <Input id="social_facebook_app_secret" name="social_facebook_app_secret" type="password" value={settings.social_facebook_app_secret} onChange={handleInputChange} data-testid="settings-social-facebook-app-secret" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="social_facebook_page_id">Facebook Page ID</Label>
                  <Input id="social_facebook_page_id" name="social_facebook_page_id" value={settings.social_facebook_page_id} onChange={handleInputChange} data-testid="settings-social-facebook-page-id" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="social_facebook_page_access_token">Facebook Page Access Token</Label>
                  <Input id="social_facebook_page_access_token" name="social_facebook_page_access_token" type="password" value={settings.social_facebook_page_access_token} onChange={handleInputChange} data-testid="settings-social-facebook-page-token" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="social_instagram_business_account_id">Instagram Business Account ID</Label>
                  <Input id="social_instagram_business_account_id" name="social_instagram_business_account_id" value={settings.social_instagram_business_account_id} onChange={handleInputChange} data-testid="settings-social-instagram-business-id" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="social_instagram_access_token">Instagram Access Token</Label>
                  <Input id="social_instagram_access_token" name="social_instagram_access_token" type="password" value={settings.social_instagram_access_token} onChange={handleInputChange} data-testid="settings-social-instagram-token" />
                </div>
              </div>

              <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-4 text-sm text-[hsl(var(--muted-foreground))] space-y-1">
                <p><strong className="text-[hsl(var(--foreground))]">Wichtig:</strong> Entwürfe, Freigaben und Bildwahl funktionieren sofort im Admin.</p>
                <p>Die echte Veröffentlichung und KI-Bilderzeugung werden aktiv, sobald die Zugangsdaten hier gespeichert sind.</p>
              </div>
            </CardContent>
          </Card>


          {/* Employee Permissions */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-indigo-500" />
                Mitarbeiter-Berechtigungen
              </CardTitle>
              <CardDescription>
                Legen Sie fest, welche Bereiche Mitarbeiter sehen und bearbeiten dürfen. Admins haben immer vollen Zugriff.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { key: 'dashboard', label: 'Dashboard' },
                  { key: 'bookings', label: 'Buchungen' },
                  { key: 'calendar', label: 'Kalender' },
                  { key: 'customers', label: 'Kunden' },
                  { key: 'trailers', label: 'Anhänger' },
                  { key: 'locations', label: 'Standorte' },
                  { key: 'analytics', label: 'Analytics' },
                  { key: 'audit_log', label: 'Audit-Log' },
                  { key: 'settings', label: 'Einstellungen' },
                  { key: 'role_management', label: 'Rollenverwaltung' },
                  { key: 'blog', label: 'Blog / SEO' },
                  { key: 'inquiries', label: 'Anfragen & Offerten' },
                  { key: 'social_media', label: 'Social Media' },
                  { key: 'support', label: 'Support / Queue' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg border border-[hsl(var(--border))] bg-white">
                    <span className="text-sm font-medium">{label}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newPerms = { ...settings.employee_permissions, [key]: !settings.employee_permissions[key] };
                        setSettings({ ...settings, employee_permissions: newPerms });
                      }}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        settings.employee_permissions[key] ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'
                      }`}
                      data-testid={`settings-perm-${key}-toggle`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        settings.employee_permissions[key] ? 'translate-x-4' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Abandoned Quote Discount Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-500" />
                Angebotsabbruch-Rabatt
              </CardTitle>
              <CardDescription>
                Kunden, die einen Standort angeschaut aber nicht gebucht haben, erhalten nach X Tagen automatisch einen Rabattcode per E-Mail.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Angebotsabbruch-Rabatt aktivieren</Label>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'abandoned_quote_enabled', value: !settings.abandoned_quote_enabled } })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.abandoned_quote_enabled ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'
                  }`}
                  data-testid="settings-abandoned-quote-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.abandoned_quote_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              {settings.abandoned_quote_enabled && (
                <div className="pt-3 border-t border-[hsl(var(--border))] space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Tage ohne Buchung</Label>
                      <Input
                        type="number"
                        min="1"
                        value={settings.abandoned_quote_days}
                        onChange={(e) => handleInputChange({ target: { name: 'abandoned_quote_days', value: parseInt(e.target.value) || 15 } })}
                        data-testid="settings-abandoned-days-input"
                      />
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">
                        Nach so vielen Tagen wird die E-Mail gesendet
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Rabatt (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={settings.abandoned_quote_discount_value}
                        onChange={(e) => handleInputChange({ target: { name: 'abandoned_quote_discount_value', value: parseFloat(e.target.value) || 5 } })}
                        data-testid="settings-abandoned-discount-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Gültigkeit des Codes (Tage)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={settings.abandoned_quote_code_validity_days}
                      onChange={(e) => handleInputChange({ target: { name: 'abandoned_quote_code_validity_days', value: parseInt(e.target.value) || 7 } })}
                      data-testid="settings-abandoned-validity-input"
                    />
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      Wie lange der Rabattcode nach Versand gültig ist
                    </span>
                  </div>
                  {!settings.smtp_email && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-amber-600">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>SMTP muss konfiguriert sein, damit E-Mails gesendet werden.</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
    </>
  );
};

export default SettingsTeam;
